import { Filter, X } from "lucide-react";
import { Button } from "../../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { MediaSearchParams } from "../../../types/media-adaptors";

interface VideoFilterPanelProps {
  filters: Partial<Pick<MediaSearchParams, "orientation" | "size">>;
  onFiltersChange: (filters: Partial<Pick<MediaSearchParams, "orientation" | "size">>) => void;
}

/**
 * VideoSearchFilterPanel - Filter component for video search
 * 
 * Provides filters for orientation and size to refine video search results.
 * Displays active filters and allows users to clear them.
 */
export const VideoSearchFilterPanel: React.FC<VideoFilterPanelProps> = ({
  filters,
  onFiltersChange,
}) => {
  const hasActiveFilters = filters.orientation || filters.size;

  const handleOrientationChange = (value: string) => {
    onFiltersChange({
      ...filters,
      orientation: value === "all" ? undefined : (value as "landscape" | "portrait" | "square"),
    });
  };

  const handleSizeChange = (value: string) => {
    onFiltersChange({
      ...filters,
      size: value === "all" ? undefined : (value as "small" | "medium" | "large"),
    });
  };

  const handleClearFilters = () => {
    onFiltersChange({});
  };

  return (
    <div className="flex flex-col gap-2 shrink-0">
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs font-extralight text-muted-foreground">Filters</span>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            className="h-6 px-2 text-xs font-extralight ml-auto"
          >
            <X className="h-3 w-3 mr-1" />
            Clear
          </Button>
        )}
      </div>

      <div className="flex gap-2">
        <div className="flex-1">
          <Select
            value={filters.orientation || "all"}
            onValueChange={handleOrientationChange}
          >
            <SelectTrigger className="w-full h-9 text-xs font-extralight shadow-none bg-input">
              <SelectValue placeholder="Orientation" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Orientations</SelectItem>
              <SelectItem value="landscape">Landscape</SelectItem>
              <SelectItem value="portrait">Portrait</SelectItem>
              <SelectItem value="square">Square</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1">
          <Select
            value={filters.size || "all"}
            onValueChange={handleSizeChange}
          >
            <SelectTrigger className="w-full h-9 text-xs font-extralight shadow-none bg-input">
              <SelectValue placeholder="Size" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sizes</SelectItem>
              <SelectItem value="small">Small</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="large">Large</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

